import { useSyncExternalStore } from 'react'
import type { Point } from './safe-triangle'

/**
 * Shared hover behaviour for cell preview cards, mirroring Base UI's tooltip
 * provider — which `PreviewCard` has no equivalent of, so it lives here.
 *
 * Three things come out of treating the cards as one group rather than as
 * independent popups:
 *
 * - **Skip delay.** The first card in a sweep waits out the open delay, but
 *   while one is showing — and briefly after the last closes — the next opens
 *   immediately. Otherwise reading down a list costs the delay on every row,
 *   which reads as lag rather than deliberation.
 * - **Supersession.** Each card is its own Base UI root with its own close
 *   delay, so an instant open would leave the previous card on screen beside
 *   the new one. Opening dismisses whoever else is up.
 * - **Safe triangle.** While the pointer travels from a cell to its card, the
 *   rows it crosses hold their open until it lands or leaves the cone.
 *
 * Supersession works by calling the other cards' own close callbacks rather
 * than by publishing an "active id" they each compare themselves against: a
 * card only ever acts on a live registration, so a stale entry can't leave
 * every card permanently convinced it has been superseded.
 *
 * Module state rather than a provider because the cards span sibling sections
 * (Reminders, Outbox) with no shared ancestor worth threading context through,
 * and one renderer only ever has one pointer.
 */

/** How long after the last card closes the group stays warm. */
const SKIP_DELAY_MS = 300

const openCards = new Map<string, () => void>()
let warmUntil = 0
let cooldown: ReturnType<typeof setTimeout> | null = null
const listeners = new Set<() => void>()

const emit = () => {
  for (const listener of listeners) listener()
}

const isWarm = () => openCards.size > 0 || Date.now() < warmUntil

/**
 * Register `id` as the showing card, dismissing any other. `close` is that
 * card's own hover-close callback. Returns the release callback for cleanup.
 */
export function markPreviewOpen(id: string, close: () => void): () => void {
  for (const [otherId, otherClose] of openCards) {
    if (otherId !== id) otherClose()
  }
  openCards.set(id, close)
  if (cooldown) {
    clearTimeout(cooldown)
    cooldown = null
  }
  emit()

  return () => {
    openCards.delete(id)
    if (openCards.size === 0) {
      warmUntil = Date.now() + SKIP_DELAY_MS
      // Notify again when the window lapses, so cards still mounted pick their
      // full delay back up instead of staying warm until some unrelated render.
      if (cooldown) clearTimeout(cooldown)
      cooldown = setTimeout(() => {
        cooldown = null
        emit()
      }, SKIP_DELAY_MS)
    }
    emit()
  }
}

/** Opens a card if the pointer, at `at`, is still over its cell. */
type ReplayOpen = (at: Point) => void

let triangleOwner: string | null = null
let deferredOpen: ReplayOpen | null = null

/** `id`'s card is being reached for; hover changes are gated until release. */
export function claimSafeTriangle(id: string) {
  triangleOwner = id
}

/**
 * Ends `id`'s hold. `replayAt` is where the pointer was when it gave up on the
 * card mid-trip: the last held-back open runs there, and opens only if that is
 * still over its cell. Null when the trip landed or the card closed some other
 * way (Escape, scroll, unmount), which drops it.
 */
export function releaseSafeTriangle(id: string, replayAt: Point | null) {
  if (triangleOwner !== id) {
    return
  }
  triangleOwner = null
  const replay = deferredOpen
  deferredOpen = null
  if (replayAt) {
    replay?.(replayAt)
  }
}

/**
 * Asks to apply a hover open or close for `id`; returns whether it may go ahead.
 *
 * While a safe triangle is held, its owner can't close and other cards can't
 * open. A blocked open is kept as `replayOpen` and runs when the triangle
 * releases, so the row the pointer ends up on still opens. Only the last one
 * survives, since that is the row the pointer got to.
 */
export function requestHoverChange(id: string, nextOpen: boolean, replayOpen: ReplayOpen): boolean {
  if (triangleOwner === null) {
    return true
  }
  if (triangleOwner === id) {
    return nextOpen
  }
  if (nextOpen) {
    deferredOpen = replayOpen
  }
  return !nextOpen
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** True while a card is showing, or within the grace window after the last closed. */
export function useHoverGroupWarm(): boolean {
  return useSyncExternalStore(subscribe, isWarm, () => false)
}
